import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import { AssetType } from 'contexts/filters/assetTypes'
import { useBackendFilters } from 'contexts/filters/BackendFiltersContext'
import { BackendFilterOptions } from 'contexts/filters/backendFilterTypes'
import useParsedSearchParams, { Params } from 'hooks/useParsedSearchParams'
import { countSelectedFilters } from 'utils/countSelectedFilters'

import usePersistedTransactionFilters from './usePersistedTransactionFilters'
import {
  TransactionsFilterFormFields,
  transactionsFilterFormSchema,
} from '../components/transactions-filters/types'

const defaultValues: TransactionsFilterFormFields = {
  dateRange: {},
  source: [],
  token: [],
  from: '',
  txn_id: '',
  source_type: [],
  asset_type: [],
  transaction_func: [],
  missing_price: false,
  range: [],
  type: [],
  voucher: [],
  syncStatus: [],
  select_custom_fields: [],
  number_custom_fields: [],
  string_custom_fields: [],
  category: [],
  memo: '',
  to: '',
  wallet: '',
  source_tags: [],
  amount_range_min: '',
  amount_range_max: '',
}

const useTransactionsFiltersDrawer = () => {
  const validateParams = useCallback(
    (params: Params) => transactionsFilterFormSchema.parse(params),
    []
  )

  const { setTransactionstFilterState, resetTransactionstFilterState } =
    usePersistedTransactionFilters()

  const { applyBackendFilters, filterState } = useBackendFilters()

  const { searchParams, setSearchParams } = useParsedSearchParams({
    validateParams,
  })
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const formMethods = useForm<TransactionsFilterFormFields>({
    resolver: zodResolver(transactionsFilterFormSchema),
    defaultValues,
  })

  const handleSubmit = formMethods.handleSubmit((formValues: TransactionsFilterFormFields) => {
    updateActiveFilters(formValues)
    setTransactionstFilterState(formValues)
    toggleDrawer()
  })

  const activeFilters = useMemo(() => searchParams, [searchParams])

  const updateActiveFilters = useCallback(
    (filters: TransactionsFilterFormFields) => {
      setSearchParams(filters)
    },
    [setSearchParams]
  )

  const resetActiveFilters = useCallback(() => {
    setSearchParams({})
  }, [setSearchParams])

  const resetForm = () => {
    formMethods.reset(defaultValues)
    applyBackendFilters({
      ...filterState,
      pageSize: 25,
      pageNumber: 0,
    })
    resetTransactionstFilterState()
  }

  const selectedFiltersCount = useMemo(() => {
    const { version, ...filtersForCounting } = activeFilters
    return countSelectedFilters(filtersForCounting)
  }, [activeFilters])

  const toggleDrawer = () => setIsDrawerOpen(!isDrawerOpen)

  useEffect(() => {
    formMethods.setValue('dateRange', {
      startDate: activeFilters.dateRange.startDate,
      endDate: activeFilters.dateRange.endDate,
    })
    formMethods.setValue('source', activeFilters.source)
    formMethods.setValue('token', activeFilters.token)
    formMethods.setValue('from', activeFilters.from)
    formMethods.setValue('txn_id', activeFilters.txn_id)
    formMethods.setValue('source_type', activeFilters.source_type)
    formMethods.setValue('asset_type', activeFilters.asset_type)
    formMethods.setValue('transaction_func', activeFilters.transaction_func)
    formMethods.setValue('missing_price', activeFilters.missing_price)
    formMethods.setValue('range', activeFilters.range)
    formMethods.setValue('syncStatus', activeFilters.syncStatus)
    formMethods.setValue('select_custom_fields', activeFilters.select_custom_fields)
    formMethods.setValue('number_custom_fields', activeFilters.number_custom_fields)
    formMethods.setValue('string_custom_fields', activeFilters.string_custom_fields)
    formMethods.setValue('voucher', activeFilters.voucher)
    formMethods.setValue('category', activeFilters.category)
    formMethods.setValue('type', activeFilters.type)
    formMethods.setValue('to', activeFilters.to)
    formMethods.setValue('wallet', activeFilters.wallet)
    formMethods.setValue('source_tags', activeFilters.source_tags)
    formMethods.setValue('amount_range_min', activeFilters.amount_range_min)
    formMethods.setValue('amount_range_max', activeFilters.amount_range_max)
    formMethods.setValue('memo', activeFilters.memo)

    applyBackendFilters({
      ...filterState,
      ...activeFilters,
      dateRange: [
        activeFilters.dateRange.startDate ?? null,
        activeFilters.dateRange.endDate ?? null,
      ],
      range: [activeFilters.amount_range_min || '', activeFilters.amount_range_max || ''],
      asset_type: activeFilters.asset_type as AssetType[],
    })
  }, [activeFilters, applyBackendFilters, filterState, formMethods])

  const filterStateWithUrlFilters = useMemo((): BackendFilterOptions => {
    return {
      ...filterState,
      ...activeFilters,
      dateRange: [
        activeFilters.dateRange.startDate ?? null,
        activeFilters.dateRange.endDate ?? null,
      ],
      range: [activeFilters.amount_range_min || '', activeFilters.amount_range_max || ''],
      asset_type: activeFilters.asset_type as AssetType[],
      queryExecutionId: filterState.queryExecutionId,
    }
  }, [activeFilters, filterState])

  return {
    formMethods,
    isFormDirty: formMethods.formState.isDirty,
    resetForm,
    handleSubmit,
    toggleDrawer,
    isDrawerOpen,
    activeFilters,
    resetActiveFilters,
    selectedFiltersCount,
    filterStateWithUrlFilters,
  }
}

export default useTransactionsFiltersDrawer
